import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import StoryboardPreview from '@/components/StoryboardPreview';
import ImageGenerationModal from '@/components/ImageGenerationModal';
import { fetchVideoProject, ScriptResponse } from '@/api';

export default function StoryboardPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<any>(null);
  const [script, setScript] = useState<ScriptResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<'biblical' | 'african' | 'modern' | null>(null);
  const [showImageGenModal, setShowImageGenModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    const loadProject = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await fetchVideoProject(id);
        console.log('Fetched project data:', data);
        setProject(data);

        // Parse script from database format
        if (data.script) {
          console.log('Found script in project:', typeof data.script);
          const scriptData = typeof data.script === 'string' ? JSON.parse(data.script) : data.script;
          
          // Parse scenes from assets if they exist
          let scenes: any[] = [];
          if (data.assets) {
            const assets = typeof data.assets === 'string' ? JSON.parse(data.assets) : data.assets;
            scenes = assets.scenes || [];
          }

          console.log('Parsed scenes:', scenes);
          setScript({
            script: scriptData,
            scenes: scenes || [],
            metadata: data.metadata || {}
          } as any);
        } else {
          console.log('No script found in project data');
        }
      } catch (err) {
        console.error('Failed to load project:', err);
        setError('Failed to load project');
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [id]);

  const handleRegenerateScript = async () => {
    if (!id || !project) return;

    try {
      setIsGenerating(true);
      const response = await fetch('/api/ai/generate-script', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          topic: project.title || project.description || 'Video Content',
          duration: '3:00', // Default 3 minutes
          audience: 'general',
          style: 'engaging'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate script');
      }

      const result = await response.json();
      if (result.success) {
        // Reload the page to show the new script and scenes
        window.location.reload();
      }
    } catch (err) {
      console.error('Failed to regenerate script:', err);
      setError('Failed to generate script. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center animate-pulse">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Loading storyboard...</h2>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4v2m0 4v2M7 19H5a2 2 0 01-2-2V7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Error</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error || 'Failed to load project'}</p>
          <button
            onClick={() => navigate('/projects')}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  // Check if we have a script with scenes
  const hasScenes = script && script.scenes && script.scenes.length > 0;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(`/projects/${id}`)}
            className="flex items-center gap-2 text-indigo-500 hover:text-indigo-600 font-medium mb-4"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Project
          </button>

          <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-6 mb-6">
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
              {project?.title || 'Storyboard'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {project?.description || 'Visual breakdown of your script'}
            </p>
          </div>
        </div>

        {/* No Script State */}
        {!hasScenes && (
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-8">
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-gradient-to-r from-yellow-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Script Generated Yet</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">Create a scene-based script with AI to visualize your content as a storyboard.</p>
              
              <button
                onClick={handleRegenerateScript}
                disabled={isGenerating}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 transition-colors font-medium inline-flex items-center gap-2"
              >
                {isGenerating ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Generate Script with Scenes
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Storyboard with scenes */}
        {hasScenes && (
          <>
            <StoryboardPreview 
              scenes={script?.scenes || []} 
              projectTitle={project?.title || 'Untitled Project'}
              onSelectStyle={setSelectedStyle}
            />

            {/* Next Steps (when style selected) */}
            {selectedStyle && (
              <div className="mt-8 bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-6">
                <div className="flex items-start justify-between mb-6">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Next Steps</h3>
                  <div className="flex gap-3">
                    <button
                      onClick={() => navigate(`/projects/${id}/editor`)}
                      className="px-4 py-2 bg-gray-200 dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors font-medium inline-flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit Scenes
                    </button>
                    <button
                      onClick={() => setShowImageGenModal(true)}
                      className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-colors font-medium inline-flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Generate Images
                    </button>
                  </div>
                </div>
                <ol className="space-y-3 text-gray-700 dark:text-gray-300">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-sm font-bold">1</span>
                    <span><strong>Generate Images:</strong> AI will create images for each scene using your selected style</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-sm font-bold">2</span>
                    <span><strong>Edit & Adjust:</strong> Fine-tune scenes in the professional editor workspace</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-sm font-bold">3</span>
                    <span><strong>Add Animations:</strong> Apply Ken Burns effects and transitions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-sm font-bold">4</span>
                    <span><strong>Record Voice:</strong> Add your narration aligned with scenes</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-sm font-bold">5</span>
                    <span><strong>Export Video:</strong> Compose everything into your final video</span>
                  </li>
                </ol>
              </div>
            )}

            {/* Image Generation Modal */}
            {script && (
              <ImageGenerationModal
                open={showImageGenModal}
                onClose={() => setShowImageGenModal(false)}
                scenes={script.scenes || []}
                projectId={id || ''}
                projectTitle={project?.title || 'Untitled'}
                selectedStyle={selectedStyle as 'biblical' | 'african' | 'modern'}
                onGenerationStart={() => setIsGenerating(true)}
                onGenerationComplete={(generatedScenes) => {
                  setScript(prev => prev ? { ...prev, scenes: generatedScenes } : null);
                  setIsGenerating(false);
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
